import { Box, Stack, Typography, useTheme } from "@mui/material";
import { Groups, Notifications, Assignment, Rocket } from "@mui/icons-material";
import React, { useContext, useState, useEffect } from 'react';
import { Context as AppContext } from "../../context/AppContext";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import ListOfMentors from "./ListOfMentors";
import './mentorHomepage.css';

const Mentors = () => {
    const theme = useTheme();
    const { state: { currentUser, mentors }, updateUserData, getMentors } = useContext(AppContext);
    const [applicableMentors, setApplicableMentors] = useState([])

    useEffect(() => {
        setApplicableMentors(mentors.filter(mentor => mentor.mentees?.length === 0))
    }, [mentors])

    useEffect(() => {
        if (mentors.length === 0) {
            getMentors()
        }
    }, [])

    return (
        <MainLayout>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#fafbfc',
                fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
            }}>
                <Box sx={{
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 3 }
                }}>
                    {/* Welcome Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Choose Your
                            <Box component="span" sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 700
                            }}>
                                {' '}Mentor
                            </Box>
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748b',
                                fontSize: '1rem',
                                maxWidth: '600px'
                            }}
                        >
                            Connect with industry professionals who will guide your career growth and help you achieve your goals.
                        </Typography>
                    </Box>

                    {applicableMentors.length > 0 ? (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 2, md: 3 },
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    Available Mentors
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.5,
                                    bgcolor: '#f0f9ff',
                                    color: '#0c4a6e',
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    {applicableMentors.length} {applicableMentors.length === 1 ? 'Mentor' : 'Mentors'} Available
                                </Box>
                            </Box>
                            <ListOfMentors mentors={applicableMentors} />
                        </Box>
                    ) : (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                            }
                        }}>
                            <Box sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            mb: 2,
                                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                                        }}
                                    >
                                        No Mentors Available
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '1rem',
                                            maxWidth: '500px',
                                            mx: 'auto',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        All mentors are currently working with other students. Please check back later for new opportunities.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
                                    <Box sx={{
                                        width: 120,
                                        height: 120,
                                        bgcolor: '#f8fafc',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '2px solid #e2e8f0'
                                    }}>
                                        <Groups sx={{ fontSize: '4rem', color: '#8b5cf6' }} />
                                    </Box>
                                </Box>

                                {/* Feature Cards */}
                                <Box sx={{
                                    pt: 3,
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                    gap: 3
                                }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0f9ff',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Notifications sx={{ fontSize: '1.5rem', color: '#3b82f6' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Get Notified
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            We'll let you know when mentors become available
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0fdf4',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Assignment sx={{ fontSize: '1.5rem', color: '#10b981' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Prepare Profile
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Complete your profile to stand out
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#fefce8',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Rocket sx={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Start Journey
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Begin your professional development
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </MainLayout>
    );
};

export default Mentors;